import styled, { useTheme } from 'styled-components'
import ContentLoader from 'react-content-loader'

import { useVaultInfos } from '@/shared/kai/hooks/useVaultInfo'
import { Button } from 'components/Button/Button'
import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { IconArrowUp } from 'components/Icons/IconArrowUp'
import { Typography } from 'components/Typography/Typography'
import { ConnectWallet } from 'modules/ConnectWallet/ConnectWallet'
import { formatNumberWithCommas } from 'utils/helpers'
import { useSuiPrice } from 'utils/useSuiPrice'
import { useIsMobile } from 'utils/useIsMobile'
import { CoinInfo, PhantomTypeArgument, USDC, VaultInfo, VAULTS } from '@kunalabs-io/kai'
import { useTokenPriceBatch } from 'utils/useTokenPrice'
import { usePositionsGlobalMetrics } from '@/shared/kai/hooks/usePositionsGlobalMetrics'
import { useMemo } from 'react'
import { compressSuiType } from '@/shared/gen/_framework/util'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'

const Wrapper = styled.div`
  padding-top: 180px;
  width: 100%;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    max-width: 1200px;
    width: 100%;
    margin: auto;
  }
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding-top: 138px;
  }
`

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10%;
  margin: auto;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    padding: 0 20px;
    text-align: center;
  }
`

const Title = styled(Typography)`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Subtitle2}px;
  }
`

const TitleContainer = styled.div`
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    width: 60%;
  }
`
const Info = styled(Typography)`
  margin: 24px 0;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    font-size: ${p => p.theme.typography.size.Caption1}px;
    margin: 16px 0;
  }
`

const SuiContainer = styled.div<{ $isPositive: boolean; $isLoading: boolean }>`
  padding: 16px 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 16px;
  border: 1px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.coinContainerBg};
  width: 240px;
  & > div {
    align-items: center;
    display: flex;
  }
  svg {
    stroke: ${p =>
      p.$isLoading ? 'none' : p.$isPositive ? p.theme.colors.text.success : p.theme.colors.text.danger} !important;
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-left: auto;
    margin-right: auto;
  }
`

const SuiPercentageChange = styled(Typography)<{ $isPositive: boolean }>`
  color: ${p => (p.$isPositive ? p.theme.colors.text.success : p.theme.colors.text.danger)};
`

const Metrics = styled.div`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    & > div {
      width: 103px;
      height: 103px;
    }
  }
`

const MetricContainer = styled.div`
  display: flex;
  padding: 32px 54px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  border-radius: 24px;
  border: 1px solid ${p => p.theme.colors.divider};
  &:hover {
    background-color: ${p => p.theme.colors.background.active};
  }
  background-color: ${p => p.theme.colors.background.primary};

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    & > div {
      font-size: ${p => p.theme.typography.size.Body}px;
      width: 100%;
    }
    padding: 0;
  }
`

const ConnectContainer = styled.div<{ $hide: boolean }>`
  display: flex;
  align-items: center;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-bottom: 48px;
    justify-content: center;
  }

  ${p => p.$hide && `display: none;`}
`

const SuiPriceContentLoader = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={130}
      height={32}
      viewBox="0 0 130 32"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="0" rx="4" ry="4" width="130" height="32" />
    </ContentLoader>
  )
}

const TvlContentLoader = () => {
  const isMobile = useIsMobile()
  const theme = useTheme()

  if (isMobile) {
    return (
      <div>
        <ContentLoader
          speed={2}
          width={90}
          height={21}
          viewBox="0 0 90 21"
          backgroundColor={theme.colors.contentLoader.backgroundColor}
          foregroundColor={theme.colors.contentLoader.foregroundColor}
        >
          <rect x="0" y="0" rx="4" ry="4" width="90" height="21" />
        </ContentLoader>
      </div>
    )
  }
  return (
    <>
      <ContentLoader
        speed={2}
        width={265}
        height={52}
        viewBox="0 0 265 52"
        backgroundColor={theme.colors.contentLoader.backgroundColor}
        foregroundColor={theme.colors.contentLoader.foregroundColor}
      >
        <rect x="0" y="0" rx="4" ry="4" width="265" height="52" />
      </ContentLoader>
      <div style={{ height: 20 }} />
      <ContentLoader
        speed={2}
        width={190}
        height={24}
        viewBox="0 0 190 24"
        backgroundColor={theme.colors.contentLoader.backgroundColor}
        foregroundColor={theme.colors.contentLoader.foregroundColor}
      >
        <rect x="0" y="0" rx="4" ry="4" width="190" height="24" />
      </ContentLoader>
    </>
  )
}

interface Props {
  onHowToConnectClick: () => void
}

export const TitleMetricsSection = ({ onHowToConnectClick }: Props) => {
  const walletAddress = useCurrentWalletAddress()

  const { suiPrice, suiPriceChange24h, isLoading: isLoadingSuiPrice } = useSuiPrice()

  const vaultTokens = Array.from(
    new Set(Object.values(VAULTS).map(vault => vault.T))
  ) as CoinInfo<PhantomTypeArgument>[]
  const pricesRes = useTokenPriceBatch(vaultTokens, USDC)

  const prices = useMemo(() => {
    const prices = new Map<string, number>()
    pricesRes.forEach(({ data }) => {
      if (data) {
        prices.set(data.X.typeName, data.human.toNumber())
      }
    })
    return prices
  }, [pricesRes])

  const vaultInfos = useVaultInfos(
    Array.from(Object.values(VAULTS)) as VaultInfo<PhantomTypeArgument, PhantomTypeArgument>[]
  )

  const tvlIsLoading = vaultInfos.some(vaultInfo => vaultInfo.isLoading) || pricesRes.some(res => res.isLoading)

  let tvl = 0
  for (const info of vaultInfos) {
    if (!info.vault || !info.tvl) {
      continue
    }

    const price = prices.get(compressSuiType(info.vault.$typeArgs[0])) || 0
    tvl += info.tvl.toNumber() * price
  }
  const tvlDisplay = getFormattedUsdValue(tvl, { decimals: 0, compact: false })

  const { data: positionsGlobalMetrics, isLoading: positionsGlobalMetricsLoading } = usePositionsGlobalMetrics()

  let suiIsUp: boolean | undefined = undefined
  let change24hDisplay: string | undefined = undefined
  if (suiPriceChange24h !== undefined) {
    suiIsUp = suiPriceChange24h >= 0
    change24hDisplay = `${suiPriceChange24h > 0 ? '+' : ''}${suiPriceChange24h.toFixed(2)}% (1d)`
  }

  let suiPriceDisplay = ''
  if (suiPrice !== undefined) {
    suiPriceDisplay = `$${formatNumberWithCommas(suiPrice, 2)}`
  }

  const theme = useTheme()

  return (
    <Wrapper>
      <Container>
        <TitleContainer>
          <SuiContainer $isPositive={!!suiIsUp} $isLoading={isLoadingSuiPrice}>
            <div>
              <img src="/sui-logo-1.png" style={{ marginRight: 8 }} />
              <Typography variant="body" color="secondary">
                SUI
              </Typography>
            </div>
            {isLoadingSuiPrice ? (
              <SuiPriceContentLoader />
            ) : (
              <div>
                <Typography variant="subtitle2" fontWeight="semiBold" style={{ marginRight: 8 }}>
                  {suiPriceDisplay}
                </Typography>
                {suiIsUp ? <IconArrowUp /> : <IconArrowDown />}
                <SuiPercentageChange $isPositive={!!suiIsUp} variant="caption" color="secondary">
                  {change24hDisplay}
                </SuiPercentageChange>
              </div>
            )}
          </SuiContainer>
          <Title variant="title">
            Navigating <span style={{ fontWeight: 600, color: theme.colors.text.active }}>DeFi</span>
          </Title>
          <Title variant="title">
            with&nbsp;
            <span style={{ fontWeight: 600, color: theme.colors.text.active }}>Confidence</span>
          </Title>
          <Info variant="label2" fontWeight="light" color="secondary">
            Choose steady growth with <span style={{ fontWeight: 600 }}>Single Asset Vaults</span> or boost returns
            with&nbsp;
            <span style={{ fontWeight: 600 }}>Leveraged LP Vaults</span> — yield solutions for&nbsp;
            <span style={{ fontWeight: 600 }}>every investment style</span>.
          </Info>
          <ConnectContainer $hide={!!walletAddress}>
            <ConnectWallet menuStyles={{ right: 'auto' }} />
            <Button style={{ marginLeft: 16 }} outlined onClick={onHowToConnectClick}>
              How to Connect
            </Button>
          </ConnectContainer>
        </TitleContainer>
        <div>
          <Metrics>
            <MetricContainer style={{ marginBottom: 32 }}>
              {tvlIsLoading ? (
                <TvlContentLoader />
              ) : (
                <>
                  <Typography variant="title2" fontWeight="semiBold">
                    {tvlDisplay}
                  </Typography>
                  <Typography variant="description" fontWeight="light">
                    Total Value Locked (TVL)
                  </Typography>
                </>
              )}
            </MetricContainer>
            <MetricContainer style={{ marginBottom: 32 }}>
              {positionsGlobalMetricsLoading ? (
                <TvlContentLoader />
              ) : (
                <>
                  {positionsGlobalMetrics && (
                    <>
                      <Typography variant="title2" fontWeight="semiBold">
                        {getFormattedUsdValue(positionsGlobalMetrics.totalBorrowedUsd, { decimals: 0, compact: false })}
                      </Typography>
                      <Typography variant="description" fontWeight="light">
                        Total borrowed
                      </Typography>
                    </>
                  )}
                </>
              )}
            </MetricContainer>
            <MetricContainer>
              {positionsGlobalMetricsLoading ? (
                <TvlContentLoader />
              ) : (
                <>
                  <Typography variant="title2" fontWeight="semiBold">
                    {positionsGlobalMetrics?.activePositionsCount}
                  </Typography>
                  <Typography variant="description" fontWeight="light">
                    Active positions
                  </Typography>
                </>
              )}
            </MetricContainer>
          </Metrics>
        </div>
      </Container>
    </Wrapper>
  )
}
